import { Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject, Subscription, firstValueFrom } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AuthLayoutComponent } from '../../shared/layouts/auth-layout.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { ToastService } from '../../shared/services/toast.service';
import { LoginService } from '../../core/services/login.service';
import { AuthService } from '../../core/services/auth.service';
import { MceRedirectProductService } from '../../core/services/mce-redirect-product.service';
import { RegisterService } from '../../core/services/register.service';
import { ExternalChannelParentMessageService } from '../../core/services/external-channel-parent-message.service';
import { ChannelEventsService } from '../../core/services/channel-events.service';
import { HeaderService } from '../../shared/services/header.service';
import {
  ViaCEPService,
  ViaCEPResponse,
} from '../../core/services/viacep.service';
import { RegisterStep2Component } from '../register/steps/step2/register-step2.component';
import {
  BRAZIL_STATES,
  BrazilState,
} from '../../shared/constants/brazil-states.constant';
import { CustomValidators } from '../../shared/validators/custom-validators';
import { IBGECity } from '../../shared/interfaces/ibge-city.interface';
import {
  ExternalChannelAccessResponse,
  ExternalChannelCompleteRegisterRequest,
} from '../../core/models/login.model';
import { UserFacade } from '../../core/store/user/user.facade';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { AvailableOffersFacade } from '../../core/store/available-offers/available-offers.facade';
import {
  STORAGE_EXTERNAL_CHANNEL_ACCESS,
  clearExternalChannelSessionStorage,
  constructFinalRedirectWithToken,
  getExternalChannelMobileDocument,
  getPostAddressCompletionRoute,
  hydrateExternalChannelSession,
  isExternalChannelMobileType,
  readExternalChannelAccessFromSession,
  readStoredExternalChannelCampaign,
  readStoredExternalChannelOrigin,
  withPersistedExternalChannelOrigin,
} from '../../core/utils/external-channel-access.util';

const CLARO_EMPRESAS_HOME_URL = `${environment.mceRedirectUrl}/#/HomeLogada`;

@Component({
  selector: 'umkt-external-channel-complete-address',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule,
    AuthLayoutComponent,
    LoadingComponent,
    RegisterStep2Component,
    I18nPipe,
  ],
  templateUrl: './external-channel-complete-address.component.html',
  styleUrls: ['../../../assets/style/login-register.component.scss'],
})
export class ExternalChannelCompleteAddressComponent implements OnInit, OnDestroy {
  addressForm!: FormGroup;
  brazilStates: BrazilState[] = BRAZIL_STATES;

  loading = false;
  submitting = false;
  showCitySuggestions = false;
  filteredCities: string[] = [];
  selectedSuggestionIndex = -1;
  citySelectedFromList = false;

  private externalAccessData: ExternalChannelAccessResponse | null = null;
  private cities: string[] = [];
  private selectedState = '';
  private cityAutoFilled = false;
  private isProgrammaticCityUpdate = false;
  private isProgrammaticStateUpdate = false;
  private cityValidationTimeout: ReturnType<typeof setTimeout> | null = null;
  private cepSubscription: Subscription | null = null;
  private citiesSubscription: Subscription | null = null;
  private addressCompleted = false;
  private readonly destroy$ = new Subject<void>();
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private http = inject(HttpClient);
  private loginService = inject(LoginService);
  private authService = inject(AuthService);
  private mceRedirectProductService = inject(MceRedirectProductService);
  private registerService = inject(RegisterService);
  private viaCEPService = inject(ViaCEPService);
  private toastService = inject(ToastService);
  private i18nService = inject(I18nService);
  private userFacade = inject(UserFacade);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private cartFacade = inject(CartFacade);
  private headerService = inject(HeaderService);
  private externalChannelParentMessageService = inject(ExternalChannelParentMessageService);
  private channelEventsService = inject(ChannelEventsService);
  private platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    this.externalAccessData = readExternalChannelAccessFromSession();

    if (!this.externalAccessData) {
      this.router.navigate(['/']);
      return;
    }

    this.initializeForm();
    this.setupFormListeners();
    this.prefillFromStoredAddress();
  }

  ngOnDestroy(): void {
    this.cepSubscription?.unsubscribe();
    this.citiesSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();

    if (this.cityValidationTimeout) {
      clearTimeout(this.cityValidationTimeout);
    }
  }

  handleLeaveWithoutCompletion(): void {
    if (this.addressCompleted) {
      return;
    }

    clearExternalChannelSessionStorage();
    this.authService.logout();
    this.cartFacade.clearCart();
    this.headerService.resetActiveTopNavItem();
  }

  goBack(): void {
    this.handleLeaveWithoutCompletion();

    if (isPlatformBrowser(this.platformId)) {
      if (this.externalChannelParentMessageService.notifyHubClosed()) {
        return;
      }

      window.location.href = CLARO_EMPRESAS_HOME_URL;
      return;
    }

    this.router.navigate(['/']);
  }

  submitAddress = (): void => {
    void this.handleSubmit();
  };

  preventLeadingSpace = (event: KeyboardEvent): void => {
    const input = event.target as HTMLInputElement;
    if (event.key === ' ' && input.selectionStart === 0) {
      event.preventDefault();
    }
  };

  onCityKeydown = (event: KeyboardEvent): void => {
    if (!this.showCitySuggestions || this.filteredCities.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.min(
          this.selectedSuggestionIndex + 1,
          this.filteredCities.length - 1
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.max(
          this.selectedSuggestionIndex - 1,
          -1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedSuggestionIndex >= 0) {
          this.selectCity(this.filteredCities[this.selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        this.showCitySuggestions = false;
        this.selectedSuggestionIndex = -1;
        break;
    }
  };

  selectCity = (city: string): void => {
    this.addressForm.get('cidade')?.setValue(city);
    this.showCitySuggestions = false;
    this.filteredCities = [];
    this.selectedSuggestionIndex = -1;
    this.citySelectedFromList = true;
    this.clearCityError();
  };

  isFromMobileFlow = (): boolean => false;

  goToLogin = (): void => {
    this.router.navigate(['/login']);
  };

  private initializeForm(): void {
    this.addressForm = this.fb.group({
      cep: ['', [Validators.required, CustomValidators.cep]],
      endereco: [
        { value: '', disabled: true },
        [
          Validators.required,
          CustomValidators.noWhitespaceOnly,
          Validators.maxLength(255),
        ],
      ],
      numero: ['', [Validators.required, CustomValidators.noWhitespaceOnly]],
      complemento: ['', CustomValidators.noWhitespaceOnly],
      bairro: [
        { value: '', disabled: true },
        [
          Validators.required,
          CustomValidators.noWhitespaceOnly,
          Validators.maxLength(100),
        ],
      ],
      cidade: [{ value: '', disabled: true }, [Validators.required]],
      estado: [{ value: '', disabled: true }, Validators.required],
    });
  }

  private prefillFromStoredAddress(): void {
    const storedZipCode =
      this.externalAccessData?.userRequest?.addresses?.[0]?.zipCode || '';
    const formattedCep = this.registerService.formatCEP(storedZipCode);

    if (!formattedCep || !this.viaCEPService.validarCEP(formattedCep)) {
      return;
    }

    this.addressForm.patchValue(
      { cep: formattedCep },
      { emitEvent: false }
    );
    this.buscarEndereco(formattedCep);
  }

  private setupFormListeners(): void {
    this.addressForm
      .get('cep')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(cep => {
        if (cep) {
          const formatted = this.viaCEPService.formatarCEP(cep);
          if (formatted !== cep) {
            this.addressForm.patchValue({ cep: formatted }, { emitEvent: false });
            cep = formatted;
          }
        }

        if (!cep) {
          this.clearAddressForm();
          return;
        }

        if (cep && this.viaCEPService.validarCEP(cep)) {
          this.buscarEndereco(cep);
        }
      });

    this.addressForm
      .get('estado')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(stateValue => {
        if (this.isProgrammaticStateUpdate) {
          return;
        }

        this.selectedState = stateValue || '';
        const cityControl = this.addressForm.get('cidade');

        if (!this.isProgrammaticCityUpdate) {
          cityControl?.setValue('');
          this.showCitySuggestions = false;
          this.filteredCities = [];
        }

        if (stateValue) {
          cityControl?.enable();
          this.loadCitiesFromIBGE(stateValue);
        } else {
          this.citiesSubscription?.unsubscribe();
          cityControl?.disable();
          this.cities = [];
        }
      });

    this.addressForm
      .get('cidade')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (this.isProgrammaticCityUpdate || this.cityAutoFilled) {
          return;
        }

        this.clearCityError();
        this.citySelectedFromList = false;

        const cityValue = (value || '').trim();
        if (!cityValue) {
          this.showCitySuggestions = false;
          this.filteredCities = [];
          return;
        }

        this.filteredCities = this.cities
          .filter(city => city.toLowerCase().includes(cityValue.toLowerCase()))
          .slice(0, 10);

        this.showCitySuggestions = this.filteredCities.length > 0;
        this.selectedSuggestionIndex = -1;
        this.validateCityWithDebounce(cityValue);
      });
  }

  private async handleSubmit(): Promise<void> {
    if (this.submitting) {
      return;
    }

    if (this.addressForm.invalid) {
      this.showFormErrors();
      return;
    }

    const cityValue = this.addressForm.get('cidade')?.value;
    const stateValue = this.addressForm.get('estado')?.value;

    if (!this.citySelectedFromList) {
      this.toastService.showError(
        this.i18nService.instant('pages.register.messages.selectCity'),
        4000,
        'top-center'
      );
      return;
    }

    if (cityValue && stateValue) {
      const isCityValid = await this.validateCityInState(stateValue, cityValue);
      if (!isCityValid) {
        return;
      }
    }

    if (!this.externalAccessData) {
      this.router.navigate(['/']);
      return;
    }

    const formValue = this.addressForm.getRawValue();
    const payload: ExternalChannelCompleteRegisterRequest = {
      ...this.externalAccessData,
      completeAddress: 'true',
      address: {
        street: (formValue.endereco || '').trim(),
        neighborhood: (formValue.bairro || '').trim(),
        city: (formValue.cidade || '').trim(),
        state: (formValue.estado || '').trim(),
        country: 'BR',
        zipCode: this.registerService.formatCEP(formValue.cep || ''),
        complement: (formValue.complemento || '').trim(),
        number: (formValue.numero || '').trim(),
      },
    };

    this.submitting = true;

    this.loginService
      .completeExternalChannelRegister(payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const updatedResponse = withPersistedExternalChannelOrigin(
            {
              ...response,
              completeAddress: 'true',
            },
            readStoredExternalChannelOrigin()
          );

          if (isPlatformBrowser(this.platformId)) {
            try {
              sessionStorage.setItem(
                STORAGE_EXTERNAL_CHANNEL_ACCESS,
                JSON.stringify(updatedResponse)
              );
            } catch {
              // ignore
            }
          }

          this.addressCompleted = true;
          this.hydrateExternalChannelLogin(updatedResponse);
          this.bootstrapMobileOffersIfNeeded(updatedResponse);
          this.cartFacade.clearExternalChannelCart();
          this.submitting = false;
          this.continueAfterAddressComplete(updatedResponse);
        },
        error: () => {
          this.submitting = false;
          this.toastService.showError(
            this.i18nService.instant(
              'pages.externalChannelCompleteAddress.submitError'
            ),
            5000,
            'top-center'
          );
        },
      });
  }

  private hydrateExternalChannelLogin(
    response: ExternalChannelAccessResponse
  ): void {
    const { user } = hydrateExternalChannelSession(
      response,
      this.authService,
      this.userFacade,
      isPlatformBrowser(this.platformId)
    );

    if (response.firstAccess) {
      const name =
        `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
        user?.displayName ||
        '';
      this.channelEventsService
        .sendUserRegisteredEvent(
          name,
          user?.document || '',
          user?.email || ''
        )
        .subscribe();
    }

    const campaign = readStoredExternalChannelCampaign();

    this.channelEventsService
      .sendUserLoginEvent({
        method: 'EXTERNAL_CHANNEL_ACCESS',
        userUuid: user?.uuid,
        login: user?.email || user?.login || user?.document,
        email: user?.email,
        ...(campaign ? { campaign } : {}),
      })
      .subscribe();
  }

  /** SUE = login móvel: seleciona CNPJ e carrega available-offers. */
  private bootstrapMobileOffersIfNeeded(
    response: ExternalChannelAccessResponse
  ): void {
    if (!isExternalChannelMobileType(response.type)) {
      return;
    }

    const clean = getExternalChannelMobileDocument(response);
    if (!clean) {
      return;
    }

    this.authService.syncSelectedDocument(clean);
    this.availableOffersFacade.loadAvailableOffers(clean);
  }

  private continueAfterAddressComplete(
    response: ExternalChannelAccessResponse
  ): void {
    this.mceRedirectProductService
      .tryAddFromStoredIntent()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(result => {
        if (result.handled) {
          this.router.navigate([result.success ? '/checkout' : '/']);
          return;
        }

        const redirect = constructFinalRedirectWithToken();
        if (redirect) {
          if (redirect.queryParams) {
            this.router.navigate(redirect.commands, { queryParams: redirect.queryParams });
          } else {
            this.router.navigate(redirect.commands);
          }
          return;
        }

        this.router.navigate([getPostAddressCompletionRoute(response)]);
      });
  }

  private buscarEndereco(cep: string): void {
    this.cepSubscription?.unsubscribe();
    this.loading = true;
    this.showCitySuggestions = false;

    this.cepSubscription = this.viaCEPService.buscarCEP(cep)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ViaCEPResponse) => {
          this.fillAddressForm(response);
          this.loading = false;
          this.toastService.showSuccess(
            this.i18nService.instant('pages.register.messages.addressFound'),
            3000,
            'top-center'
          );
        },
        error: () => {
          this.loading = false;
          this.toastService.showError(
            this.i18nService.instant('pages.register.messages.cepNotFound'),
            5000,
            'top-center'
          );
          this.clearAddressForm();
          this.enableAddressFields();
        },
      });
  }

  private fillAddressForm(response: ViaCEPResponse): void {
    this.enableAddressFields();
    this.isProgrammaticCityUpdate = true;

    this.addressForm.patchValue(
      {
        endereco: response.logradouro,
        bairro: response.bairro,
      },
      { emitEvent: false }
    );

    this.isProgrammaticStateUpdate = true;
    this.addressForm
      .get('estado')
      ?.setValue(response.uf, { emitEvent: false });
    this.selectedState = response.uf || '';
    setTimeout(() => {
      this.isProgrammaticStateUpdate = false;
    }, 0);

    const cidadeValue = response.localidade;
    this.addressForm.get('cidade')?.setValue(cidadeValue, { emitEvent: false });
    this.addressForm.get('cidade')?.updateValueAndValidity({ emitEvent: false });

    this.citySelectedFromList = true;
    this.cityAutoFilled = true;
    this.clearCityError();

    if (response.uf) {
      this.loadCitiesFromIBGE(response.uf);
    }

    setTimeout(() => {
      this.disableAddressFields();
      setTimeout(() => {
        this.isProgrammaticCityUpdate = false;
      }, 100);
    }, 50);
  }

  private loadCitiesFromIBGE(stateCode: string): void {
    this.citiesSubscription?.unsubscribe();

    const apiUrl = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`;

    this.citiesSubscription = this.http
      .get<IBGECity[]>(apiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          if (stateCode !== this.selectedState) {
            return;
          }

          this.cities = data.map(city => city.nome).sort();
        },
        error: () => {
          if (stateCode !== this.selectedState) {
            return;
          }

          this.cities = [];
          this.toastService.showError(
            this.i18nService.instant('pages.register.messages.errorLoadingCities'),
            5000,
            'top-center'
          );
        },
      });
  }

  private disableAddressFields(): void {
    this.addressForm.get('endereco')?.disable();
    this.addressForm.get('bairro')?.disable();
    this.isProgrammaticStateUpdate = true;
    this.addressForm.get('estado')?.disable({ emitEvent: false });
    setTimeout(() => {
      this.isProgrammaticStateUpdate = false;
    }, 0);
  }

  private enableAddressFields(): void {
    this.addressForm.get('endereco')?.enable();
    this.addressForm.get('bairro')?.enable();
    this.addressForm.get('estado')?.enable();
    this.addressForm.get('cidade')?.enable();
  }

  private clearAddressForm(): void {
    this.cepSubscription?.unsubscribe();
    this.citiesSubscription?.unsubscribe();
    this.loading = false;

    this.addressForm.patchValue(
      {
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
      },
      { emitEvent: false }
    );

    this.cities = [];
    this.filteredCities = [];
    this.selectedState = '';
    this.citySelectedFromList = false;
    this.cityAutoFilled = false;
  }

  private validateCityWithDebounce(cityName: string): void {
    if (this.cityValidationTimeout) {
      clearTimeout(this.cityValidationTimeout);
    }

    this.cityValidationTimeout = setTimeout(() => {
      if (this.selectedState && cityName.trim()) {
        void this.validateCityInState(this.selectedState, cityName);
      }
    }, 1000);
  }

  private async validateCityInState(
    stateCode: string,
    cityName: string
  ): Promise<boolean> {
    try {
      let cityNames: string[];

      if (this.cities.length > 0 && this.selectedState === stateCode) {
        cityNames = this.cities.map(city => city.toLowerCase());
      } else {
        const apiUrl = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`;
        const cities = await firstValueFrom(this.http.get<IBGECity[]>(apiUrl));
        cityNames = (cities ?? []).map(city => city.nome.toLowerCase());
      }

      const normalizedInput = cityName.toLowerCase().trim();
      const cityExists = cityNames.includes(normalizedInput);

      if (!cityExists) {
        this.handleInvalidCity();
        return false;
      }

      return true;
    } catch {
      this.toastService.showError(
        this.i18nService.instant('pages.register.messages.errorValidatingCity'),
        5000,
        'top-center'
      );
      return false;
    }
  }

  private handleInvalidCity(): void {
    const cityControl = this.addressForm.get('cidade');
    if (cityControl) {
      cityControl.setErrors({ invalidCity: true });
      cityControl.markAsTouched();
    }
  }

  private clearCityError(): void {
    const cityControl = this.addressForm.get('cidade');
    if (cityControl?.hasError('invalidCity')) {
      const errors = { ...cityControl.errors };
      delete errors['invalidCity'];
      cityControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }
  }

  private showFormErrors(): void {
    Object.keys(this.addressForm.controls).forEach(key => {
      const control = this.addressForm.get(key);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });

    this.toastService.showError(
      this.i18nService.instant('pages.register.messages.formError'),
      5000,
      'top-center'
    );
  }
}
